import { LocalDateTime } from '@/utils/dates'
import { AxiosInstance } from 'axios'
import axiosInstance from '.'
import { Entity, ExternalUserInfo } from './auth/register'

export interface NewPasswordRequest {
  oldPassword: string
  newPassword: string
  confirmNewPassword: string
}

export interface ExternalUserDto {
  id: number
  civilite?: string
  lastName: string
  firstName?: string
  country: string
  phone?: string
  email?: string
  customerId?: number
}

class UserApi {
  private axios: AxiosInstance

  constructor(axios: AxiosInstance) {
    this.axios = axios
  }

  public async get(): Promise<ExternalUserInfo> {
    return (await this.axios.get('/user')).data
  }

  public async updateInfo(info: ExternalUserInfo): Promise<ExternalUserInfo> {
    return (await this.axios.put('/user', info)).data
  }

  public async updatePassword(info: NewPasswordRequest): Promise<void> {
    return (await this.axios.put('/user/password', info)).data
  }

  public async generatePrivateKey(): Promise<{privateKey: string, lastChangeKeyTime: LocalDateTime}> {
    return (await this.axios.post('/me/privatekey')).data
  }

  public async sendAccountValidationEmail(email: string) {
    await this.axios.post(`/send-validate/${email}`)
  }

  public async activateAccount(token: string) {
    await this.axios.post(`/validate/${token}`)
  }

  public async getEntities(): Promise<Entity[]> {
    return (await this.axios.get('/entity')).data.content.map((e: Entity) => ({
      ...e,
      bankingInformation: e.bankingInformation ?? {},
      accountantInformation: e.accountantInformation ?? {},
    }))
  }

  public async createEntity(entity: Entity) {
    await this.axios.post('/entity', {
      id: entity.id,
      type: entity.type,
      firstName: entity.firstName,
      lastName: entity.lastName,
      civilite: entity.civilite,
      businessReason: entity.businessReason,
      paymentZone: entity.bankingInformation.paymentZoneSuggestion,
      accountOwner: entity.bankingInformation.accountOwnerSuggestion,
      accountNumber: entity.bankingInformation.accountNumberSuggestion,
      iban: entity.bankingInformation.ibanSuggestion,
      bic: entity.bankingInformation.bicSuggestion,
      bankIdentifier: entity.bankingInformation.bankIdentifierSuggestion,
      bankAddress: entity.bankingInformation.bankAddressSuggestion,
      withoutVat: entity.accountantInformation.isWithoutVatSuggestion,
      vatNumber: entity.accountantInformation.vatNumberSuggestion,
      frfNumber: entity.accountantInformation.frfNumberSuggestion,
    })
  }

  public async updateEntity(entity: Entity) {
    await this.axios.post(`/entity/${entity.id}`, {
      id: entity.id,
      type: entity.type,
      firstName: entity.firstName,
      lastName: entity.lastName,
      civilite: entity.civilite,
      businessReason: entity.businessReason,
      paymentZone: entity.bankingInformation.paymentZoneSuggestion,
      accountOwner: entity.bankingInformation.accountOwnerSuggestion,
      accountNumber: entity.bankingInformation.accountNumberSuggestion,
      iban: entity.bankingInformation.ibanSuggestion,
      bic: entity.bankingInformation.bicSuggestion,
      bankIdentifier: entity.bankingInformation.bankIdentifierSuggestion,
      bankAddress: entity.bankingInformation.bankAddressSuggestion,
      withoutVat: entity.accountantInformation.isWithoutVatSuggestion,
      vatNumber: entity.accountantInformation.vatNumberSuggestion,
      frfNumber: entity.accountantInformation.frfNumberSuggestion,
    })
  }

  public async deleteEntity(id: number) {
    await this.axios.post(`/entity/${id}/delete`)
  }
}

export const userApi = new UserApi(axiosInstance)
